
import { useState, useEffect } from 'react';

// Define scan question types
export interface ScanCategory {
  [key: string]: string[];
}

export interface ScoreOption {
  label: string;
  value: number;
  description: string;
}

export interface ScanResult {
  totalScore: number;
  maxScore: number;
  scorePercentage: number;
}

export interface ScoreInterpretation {
  title: string;
  description: string;
  recommendations: string[];
}

// Get score interpretation
export const getScoreInterpretation = (percentage: number): ScoreInterpretation => {
  if (percentage <= 40) {
    return {
      title: "Structuur ontbreekt nog",
      description: "Jullie zouden baat hebben bij een meer gestructureerde aanpak van jullie verkoopproces. Er liggen veel kansen om dit te verbeteren en zo meer klanten aan te trekken.",
      recommendations: [
        "Begin met het definiëren van een duidelijk verkoopproces",
        "Implementeer een CRM-systeem voor leadbeheer",
        "Bepaal duidelijk wie jullie ideale klant is"
      ]
    };
  } else if (percentage <= 70) {
    return {
      title: "Groeipotentieel aanwezig",
      description: "Jullie hebben al een goede basis gelegd, maar er zijn nog specifieke gebieden die aandacht nodig hebben om jullie volledige groeipotentieel te bereiken.",
      recommendations: [
        "Optimaliseer jullie bestaande processen voor lead management",
        "Creëer meer tijd voor jullie team door taken te delegeren",
        "Verfijn jullie marktpositionering verder"
      ]
    };
  } else {
    return {
      title: "Jullie zijn klaar om op te schalen via een externe belpartner",
      description: "Jullie beschikken over een solide verkoopstructuur en zijn uitstekend gepositioneerd om samen te werken met een externe belpartner voor verdere groei.",
      recommendations: [
        "Verken mogelijkheden om op te schalen zonder extra personeel",
        "Overweeg een samenwerkingsverband voor prospectie",
        "Focus op het verfijnen van jullie conversiestrategieën"
      ]
    };
  }
};

// Score options
export const scoreOptions: ScoreOption[] = [
  { label: "Nooit", value: 0, description: "Dit komt niet of zelden voor" },
  { label: "Soms", value: 1, description: "Dit komt af en toe voor" },
  { label: "Meestal", value: 3, description: "Dit is doorgaans het geval" },
  { label: "Altijd", value: 4, description: "Dit is consistent onderdeel van onze aanpak" }
];

// Questions for the scan
export const scanQuestions: ScanCategory = {
  "Verkoopstructuur": [
    "We hebben een duidelijk verkoopproces met gedefinieerde stappen.",
    "We monitoren onze verkooppijplijn consistent.",
    "We hebben duidelijke verkoopdoelstellingen voor ieder kwartaal.",
    "Ons salesteam heeft toegang tot de juiste verkooptools.",
    "We evalueren regelmatig onze verkoopprestaties en sturen bij waar nodig."
  ],
  "Tijd & capaciteit": [
    "Ons team heeft voldoende tijd om aan prospectie te doen.",
    "We hebben een gebalanceerde verdeling tussen prospectie en klantgesprekken.",
    "We kunnen snel schakelen bij verhoogde vraag naar onze diensten.",
    "We hebben een duidelijk overzicht van onze beschikbare capaciteit.",
    "We plannen proactief onze salesactiviteiten."
  ],
  "Leadbeheer": [
    "We registreren alle leads systematisch in een CRM-systeem.",
    "We volgen nauwgezet op waar leads zich bevinden in de salesfunnel.",
    "We categoriseren leads op basis van kwaliteit en prioriteit.",
    "We hebben een gestructureerd follow-up proces voor elke lead.",
    "We analyseren waarom leads wel of niet converteren."
  ],
  "Doelgroep & positionering": [
    "We hebben een duidelijk gedefinieerde ideale klant.",
    "Onze waardeproposities zijn helder en sluiten aan bij onze doelgroep.",
    "We differentiëren ons duidelijk van concurrenten.",
    "We passen onze communicatie aan per doelgroepsegment.",
    "We evalueren regelmatig of onze positionering nog relevant is."
  ],
  "Bereidheid tot externe ondersteuning": [
    "We staan open voor externe expertise om onze verkoop te optimaliseren.",
    "Ons team kan gemakkelijk samenwerken met externe partners.",
    "We hebben heldere KPI's om externe ondersteuning te evalueren.",
    "We zijn bereid processen aan te passen op basis van externe input.",
    "We investeren in lange-termijn partnerships voor duurzame groei."
  ]
};

// Custom hook to manage the scan state
export const useTelemarketingScan = () => {
  const [currentTheme, setCurrentTheme] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [scanCompleted, setScanCompleted] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [animateCard, setAnimateCard] = useState(false);
  
  // Initialize scan
  useEffect(() => {
    if (isScanning) {
      // Calculate maximum possible score
      let max = 0;
      Object.keys(scanQuestions).forEach(theme => {
        max += scanQuestions[theme].length * 4;
      });
      setMaxScore(max);
    }
  }, [isScanning]);

  // Animation effect when moving between questions
  useEffect(() => {
    if (isScanning) {
      setAnimateCard(true);
      const timer = setTimeout(() => {
        setAnimateCard(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentTheme, currentQuestionIndex, isScanning]);
  
  // Start scan
  const startScan = () => {
    setIsScanning(true);
    setCurrentTheme(Object.keys(scanQuestions)[0]);
    setCurrentQuestionIndex(0);
  };
  
  // Handle answer selection
  const handleAnswerSelect = (questionKey: string, value: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));
  };
  
  // Move to next question
  const moveToNextQuestion = () => {
    const themes = Object.keys(scanQuestions);
    const currentThemeIndex = themes.findIndex(theme => theme === currentTheme);
    const currentThemeQuestions = currentTheme ? scanQuestions[currentTheme] : [];
    
    // Check if we're at the last question of the current theme
    if (currentQuestionIndex + 1 >= currentThemeQuestions.length) {
      // Check if we're at the last theme
      if (currentThemeIndex + 1 >= themes.length) {
        // Scan completed
        setScanCompleted(true);
        
        // Calculate total score
        const total = Object.values(answers).reduce((sum, score) => sum + score, 0);
        setTotalScore(total);
      } else {
        // Move to next theme
        setCurrentTheme(themes[currentThemeIndex + 1]);
        setCurrentQuestionIndex(0);
      }
    } else {
      // Move to next question in current theme
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };
  
  // Current question key
  const getCurrentQuestionKey = () => {
    if (!currentTheme) return "";
    return `${currentTheme}_${currentQuestionIndex}`;
  };
  
  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (!isScanning || !currentTheme) return 0;
    
    const totalQuestions = Object.values(scanQuestions).flat().length;
    const currentThemeIndex = Object.keys(scanQuestions).findIndex(theme => theme === currentTheme);
    const previousQuestionsCount = Object.keys(scanQuestions)
      .slice(0, currentThemeIndex)
      .reduce((acc, theme) => acc + scanQuestions[theme].length, 0);
    
    const answeredQuestions = previousQuestionsCount + currentQuestionIndex + 1;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };
  
  // Calculate score percentage
  const getScorePercentage = () => {
    return Math.round((totalScore / maxScore) * 100);
  };

  return {
    currentTheme,
    currentQuestionIndex,
    answers,
    scanCompleted,
    totalScore,
    maxScore,
    isScanning,
    animateCard,
    startScan,
    handleAnswerSelect,
    moveToNextQuestion,
    getCurrentQuestionKey,
    getProgressPercentage,
    getScorePercentage,
    setScanCompleted
  };
};
